/**
 * Simplified authentication utilities
 * Handles token validation, refresh deduplication, and auth state clearing
 */

import Cookies from "universal-cookie";
import { jwtDecode } from "jwt-decode";
import { IPublicClientApplication } from "@azure/msal-browser";
import { EnvKeys } from "../consts";
import { getEnvValue } from "../helpers";
import { refreshToken as refreshTokenAPI, logout as logoutAPI } from "../api/auth";
import { JwtToken, RefreshToken } from "../types";

// ============================================
// Token Validation (inline, no class needed)
// ============================================

const GRACE_PERIOD_SECONDS = 60; // Account for clock skew
const REFRESH_THRESHOLD_SECONDS = 300; // Refresh 5 min before expiry
const MANUAL_SSO_LOGOUT_KEY = "manualSSOLogout";
const LAST_SSO_LOGIN_HINT_KEY = "lastSSOLoginHint";

/**
 * Check if a token is expired (with grace period)
 */
export const isTokenExpired = (expirationTimestamp: number): boolean => {
	const now = Math.floor(Date.now() / 1000);
	return now >= expirationTimestamp - GRACE_PERIOD_SECONDS;
};

/**
 * Check if token should be refreshed proactively
 */
export const shouldRefreshToken = (expirationTimestamp: number): boolean => {
	const now = Math.floor(Date.now() / 1000);
	return now >= expirationTimestamp - REFRESH_THRESHOLD_SECONDS;
};

/**
 * Check if refresh token is expired (no grace period)
 */
export const isRefreshTokenExpired = (expirationTimestamp: number): boolean => {
	const now = Math.floor(Date.now() / 1000);
	return now >= expirationTimestamp;
};

// ============================================
// Token Refresh with Deduplication
// ============================================

// Module-level promise to prevent concurrent refreshes
let activeRefreshPromise: Promise<string> | null = null;

/**
 * Refresh the JWT token, ensuring only one refresh happens at a time
 * Concurrent calls will wait for the active refresh to complete
 */
export const refreshTokenWithDedup = async (userId: number, refreshToken: string): Promise<string> => {
	// If refresh already in progress, wait for it
	if (activeRefreshPromise) {
		return activeRefreshPromise;
	}

	// Start new refresh
	activeRefreshPromise = performRefresh(userId, refreshToken);

	try {
		return await activeRefreshPromise;
	} finally {
		activeRefreshPromise = null;
	}
};

/**
 * Perform the actual token refresh
 */
const performRefresh = async (userId: number, refreshToken: string): Promise<string> => {
	const cookies = new Cookies(null, { path: "/" });

	const response = await refreshTokenAPI(userId, refreshToken);

	const returnedRefreshToken = response.getRefreshToken();
	if (!returnedRefreshToken) {
		throw new Error("Backend did not return a refresh token");
	}

	const newJWT = response.getJwt();
	if (!newJWT) {
		throw new Error("Backend did not return a JWT");
	}

	// Store new JWT
	const decodedJWT: JwtToken = jwtDecode(newJWT, { header: false });
	decodedJWT.token = newJWT;
	cookies.set(getEnvValue(EnvKeys.JwtCookieKey), decodedJWT);

	// Store new refresh token
	const newRefreshToken: RefreshToken = {
		expires: returnedRefreshToken.getExpires(),
		issuedAt: returnedRefreshToken.getIssuedAt(),
		token: returnedRefreshToken.getToken()
	};
	cookies.set(getEnvValue(EnvKeys.RefreshCookieKey), newRefreshToken);

	return newJWT;
};

// ============================================
// Auth State Clearing
// ============================================

/**
 * Clear all authentication cookies
 */
export const clearCookies = (): void => {
	const cookies = new Cookies(null, { path: "/" });
	const cookieKeys = [
		getEnvValue(EnvKeys.JwtCookieKey),
		getEnvValue(EnvKeys.RefreshCookieKey),
		getEnvValue(EnvKeys.UserCookieKey)
	].filter((key) => key.length > 0);

	cookieKeys.forEach((key) => cookies.remove(key));
};

/**
 * Clear authentication-related localStorage items
 */
export const clearAuthLocalStorage = (): void => {
	localStorage.removeItem("lastVisitedPage");
	localStorage.removeItem("sessionExpired");
};

export const hasManualSSOLogout = (): boolean => {
	return localStorage.getItem(MANUAL_SSO_LOGOUT_KEY) === "true";
};

export const markManualSSOLogout = (): void => {
	localStorage.setItem(MANUAL_SSO_LOGOUT_KEY, "true");
};

export const clearManualSSOLogout = (): void => {
	localStorage.removeItem(MANUAL_SSO_LOGOUT_KEY);
};

export const getStoredSSOLoginHint = (): string | undefined => {
	const loginHint = localStorage.getItem(LAST_SSO_LOGIN_HINT_KEY);
	return loginHint || undefined;
};

export const setStoredSSOLoginHint = (loginHint?: string): void => {
	if (!loginHint) {
		return;
	}

	localStorage.setItem(LAST_SSO_LOGIN_HINT_KEY, loginHint);
};

export const clearStoredSSOLoginHint = (): void => {
	localStorage.removeItem(LAST_SSO_LOGIN_HINT_KEY);
};

/**
 * Clear local MSAL state with package APIs while preserving the browser SSO session
 */
export const clearMSALCache = async (instance: IPublicClientApplication): Promise<void> => {
	try {
		instance.setActiveAccount(null);
		await instance.clearCache();
	} catch (error) {
		console.error("Failed to clear MSAL cache:", error);
	}
};

/**
 * Complete logout - clears all auth state
 */
export const clearAuthState = async (
	userId?: number,
	refreshToken?: string,
	msalInstance?: IPublicClientApplication
): Promise<void> => {
	// Revoke backend token (best effort)
	if (userId && refreshToken) {
		try {
			await logoutAPI(userId, refreshToken);
		} catch (error) {
			console.error("Failed to revoke backend token:", error);
		}
	}

	// Clear local state
	clearCookies();
	clearAuthLocalStorage();

	if (msalInstance) {
		markManualSSOLogout();
		await clearMSALCache(msalInstance);
	} else {
		clearManualSSOLogout();
		clearStoredSSOLoginHint();
	}
};

/**
 * Quick logout - local only, no backend call
 */
export const quickLogout = (msalInstance?: IPublicClientApplication): void => {
	clearCookies();
	clearAuthLocalStorage();
	if (msalInstance) {
		markManualSSOLogout();
		void clearMSALCache(msalInstance);
	} else {
		clearManualSSOLogout();
		clearStoredSSOLoginHint();
	}
};

/**
 * Check if user is SSO user based on externalId
 */
export const isSSOUser = (externalId?: string): boolean => {
	return !!externalId && externalId.length > 0;
};
